import { Platform } from "react-native";

import { storage } from "@/lib/storage";

/**
 * The saved appearance choice.
 *
 * Its own key and its own module, NOT a field on the auth store, which is
 * where it used to live — bolted on through a cast (`AuthStoreWithTheme`) so
 * that neither the store's type nor its `hydrate` knew about it. Nothing wrote
 * it to disk and nothing read it back, so the choice lasted exactly as long as
 * the JS context: reload the page and it was gone, and `?? 'device'` handed
 * the user back to their system theme.
 *
 * A theme is a property of a DEVICE, not of an account. Keeping it out of the
 * auth store is what stops it being entangled with sign-out — today
 * `clearLocalSession` happens to use a selective `set()` and would have left
 * it alone, but that is an accident of how that function is written, not a
 * rule anybody stated, and the next field added to the reset would break it.
 *
 * Deliberately mirrors `i18n`'s language preference, which is the same problem
 * — a device-level choice that must outlive the session and must be able to
 * mean "follow the system" explicitly.
 */
const THEME_PREFERENCE_KEY = "opncue.themePreference";

export type StoredThemePreference = "device" | "light" | "dark";

function parse(value: string | null): StoredThemePreference | null {
  return value === "device" || value === "light" || value === "dark"
    ? value
    : null;
}

/**
 * The saved choice, without waiting — web only.
 *
 * `storage` is async because SecureStore is, but its web branch is
 * `localStorage`, which is synchronous. Reading it directly here lets the very
 * first render already hold the right theme.
 *
 * That matters more than it sounds. The provider sits ABOVE the root layout's
 * boot gate on purpose, so that the splash screen is painted in the user's
 * theme rather than appearing after it; resolving the preference in an effect
 * would mean the splash renders in the SYSTEM theme and flips a frame later.
 * Someone who chose dark on a light machine would watch their app flash white
 * on every load — which is the same complaint as losing the setting, only
 * shorter.
 *
 * Returns null on native, where SecureStore has no synchronous read. The async
 * path below covers that, and a native app is not reloaded on every visit.
 */
export function readThemePreferenceSync(): StoredThemePreference | null {
  if (Platform.OS !== "web") return null;
  try {
    return parse(globalThis.localStorage?.getItem(THEME_PREFERENCE_KEY) ?? null);
  } catch {
    // Private browsing, disabled site data, an embedded webview: all of them
    // throw rather than return null, and none of them is a reason to fail to
    // render. "No saved choice" is the honest answer.
    return null;
  }
}

/** The saved choice. `device` when there is none, or when it is unreadable. */
export async function readThemePreference(): Promise<StoredThemePreference> {
  return parse(await storage.get(THEME_PREFERENCE_KEY)) ?? "device";
}

/**
 * Save the choice, including `device`.
 *
 * Written rather than removed, because "follow the system" is a CHOICE and not
 * the absence of one. Removing the key would make it indistinguishable from
 * never having chosen — which is the same value today, but stops being so the
 * moment anything wants to know whether the user has been asked.
 */
export async function writeThemePreference(
  preference: StoredThemePreference,
): Promise<void> {
  await storage.set(THEME_PREFERENCE_KEY, preference);
}
